import { DateTime } from 'luxon'
import {
  Appointment as AppointmentAPI,
  findAppointments,
  FindClinicsByIdsResponse,
} from 'patient-api-lib'
import { Appointment, AvailableCaregiver, Clinic } from '../../models/types'
import { RootState } from '../../store'
import {
  getTreatmentIdsFromTreatments,
  selectSelectedTreatment,
} from '../treatmentScreen'
import { createDateFromObject } from '../../util/date'
import { adaptAppointmentResponse } from '../appointmentScreen/appointmentUtils'
import { gbgSubAreas, stockholmSubAreas } from '../../constants/app'

export type AppointmentRequestPayload = {
  startDate: string
  endDate: string
  treatmentIds: string[]
  clinicId: string
  pageSize: number
}

export const transformUpcomingAppointments = async (
  startDate: DateTime,
  endDate: DateTime,
  treatmentIds: string[],
  clinicId: string,
  pageSize: number
) => {
  const response = await findAppointments(
    startDate.toJSDate(),
    endDate.toJSDate(),
    treatmentIds,
    [clinicId],
    '1',
    pageSize
  )

  const firstAvailableTime = response.getAppointmentsList()[0]?.getStartTime()
  if (!firstAvailableTime)
    return { count: 0, date: null, relativeDateString: null }

  const firstAvailableDate = createDateFromObject(firstAvailableTime.toObject())

  const upcomingAppointments = response
    .getAppointmentsList()
    .filter((appointment) => {
      const startTime = appointment.getStartTime()
      if (!startTime) return false

      const appointmentDate = createDateFromObject(startTime.toObject())

      return (
        firstAvailableDate.startOf('day').toMillis() ===
        appointmentDate.startOf('day').toMillis()
      )
    })

  return {
    list: response.getAppointmentsList().map((a) => a.toObject()),
    count: upcomingAppointments.length,
    date: firstAvailableDate.toISO(),
    relativeDateString:
      firstAvailableDate.toRelativeCalendar({
        base: startDate,
        unit: 'days',
      }) || '',
  }
}

export const filterAndFetchClinics = async (
  startDate: DateTime,
  endDate: DateTime,
  clinics: Clinic[],
  selectedTreatmentIds: string[],
  pageSize = 20
): Promise<Clinic[]> => {
  return Promise.all(
    clinics.map(async (clinic: Clinic) => {
      return new Promise((resolve) => {
        transformUpcomingAppointments(
          startDate,
          endDate,
          selectedTreatmentIds,
          clinic.id,
          pageSize
        ).then((upcomingAppointments) => {
          resolve({
            ...clinic,
            upcomingAppointments,
          })
        })
      })
    })
  )
}

export const getEarliestAppointment = (
  appointments: AppointmentAPI.AsObject[] | undefined
): AppointmentAPI.AsObject | null | undefined => {
  if (!appointments) return undefined

  const earliestAppointment = appointments.reduce(
    (
      earliest: AppointmentAPI.AsObject | null,
      current: AppointmentAPI.AsObject | null
    ) => {
      if (!current) return earliest
      if (!earliest) return current

      const currentStartTime = current.startTime
      const earliestStartTime = earliest.startTime

      if (!currentStartTime || !earliestStartTime) return earliest

      const currentDateTime = createDateFromObject(currentStartTime).toMillis()

      const earliestDateTime =
        createDateFromObject(earliestStartTime).toMillis()

      if (currentDateTime < earliestDateTime) return current

      return earliest
    },
    null
  )

  return earliestAppointment
}

export const getAvailableCareGivers = (
  state: RootState
): AvailableCaregiver[] => {
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const selectedClinic = state.clinicScreen.selectedClinic
  const selectedTreatmentIds = getTreatmentIdsFromTreatments(selectedTreatment)

  if (
    !selectedClinic ||
    !selectedClinic.upcomingAppointments ||
    !selectedClinic.upcomingAppointments.list
  )
    return []

  const uniqueCaregivers = [
    ...new Set(
      selectedClinic.upcomingAppointments.list.map((a) => {
        if (!a || !a.primaryCaregiver || !a.primaryCaregiver.name) return null

        return a.primaryCaregiver?.name.value
      })
    ),
  ]

  return uniqueCaregivers.map((cg) => {
    const caregiverAppointments =
      selectedClinic?.upcomingAppointments?.list?.filter(
        (a) =>
          a.primaryCaregiver?.name?.value === cg &&
          selectedTreatmentIds.includes(a.treatmentId)
      )
    return {
      name: cg,
      earliestAppointment: getEarliestAppointment(caregiverAppointments),
    }
  })
}

export const checkAppointmentOnExist = async (
  clinicId: string,
  selectedTreatment: ReturnType<typeof selectSelectedTreatment>,
  startTime?: string
): Promise<Appointment | undefined> => {
  if (!startTime) return undefined

  const startDate = DateTime.fromISO(startTime).setZone('system', {
    keepLocalTime: true,
  })

  const selectedTreatmentIds = getTreatmentIdsFromTreatments(selectedTreatment)

  const appointmentsResponse = await findAppointments(
    startDate.toJSDate(),
    startDate.plus({ second: 1 }).toJSDate(),
    selectedTreatmentIds,
    [clinicId],
    '1',
    1
  )

  return appointmentsResponse
    .getAppointmentsList()
    .map(adaptAppointmentResponse)[0]
}

export const getClinicsUpcomingAppointments = (
  appointments: AppointmentAPI[]
) => {
  const startDate = DateTime.now()
  const firstAvailableTime = appointments[0]?.getStartTime()

  if (!firstAvailableTime)
    return { count: 0, date: null, relativeDateString: null }

  const firstAvailableDate = createDateFromObject(firstAvailableTime.toObject())

  const upcomingAppointments = appointments.filter((appointment) => {
    const startTime = appointment.getStartTime()

    if (!startTime) return false

    const appointmentDate = createDateFromObject(startTime.toObject())

    return (
      firstAvailableDate.startOf('day').toMillis() ===
      appointmentDate.startOf('day').toMillis()
    )
  })

  return {
    list: appointments.map((a) => a.toObject()),
    count: upcomingAppointments.length,
    date: firstAvailableDate.toISO(),
    relativeDateString:
      firstAvailableDate.toRelativeCalendar({
        base: startDate,
        unit: 'days',
      }) || '',
  }
}

export const setCorrectClinicCity = (locality: string) => {
  if (stockholmSubAreas.includes(locality)) {
    return 'Stockholm'
  }

  if (gbgSubAreas.includes(locality)) {
    return 'Göteborg'
  }

  return locality
}

export const transformClinicResponse = (
  clinic: FindClinicsByIdsResponse.ClinicInfo | undefined
): Clinic | undefined => {
  if (!clinic) return undefined

  const clinicId = clinic.getId()
  const clinicName = clinic.getName()
  const clinicTreatments = clinic.getTreatmentsList()
  const clinicAddress = clinic.getVisitingAddress()
  const clinicLocation = clinic.getLatLng()
  const clinicPhone = clinic.getTelephone()?.getE164Number()

  const clinicLocality = clinicAddress
    ? setCorrectClinicCity(clinicAddress.getLocality())
    : ''

  return {
    id: clinicId,
    name: clinicName.replace('Aqua Dental ', ''),
    city: setCorrectClinicCity(clinicLocality),
    subCity: clinicAddress?.getLocality() || '',
    treatmentIds: clinicTreatments.map((t) => t.getTreatmentId()),
    address: `${
      clinicAddress?.getAddressLinesList()[0]
    }, ${clinicAddress?.getPostalCode()} ${clinicAddress?.getLocality()}`,
    latLng: {
      latitude: clinicLocation?.getLatitude() || 0,
      longitude: clinicLocation?.getLongitude() || 0,
    },
    phoneNumber: clinicPhone,
    distance: null,
  }
}
